import { useState, useMemo } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableFooter from "@mui/material/TableFooter";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";

import ResponsesModal from "./ResponsesModal";
import AnswersModal from "./AnswersModal";
import { numberFormatterFull } from "../../utils/Formatters";
import { getLocalTime } from "../../utils/Time";
import useSharedStyles from "../../styles/Shared";

import Header from "./Header";
import {
  Divider,
  FormControl,
  List,
  ListItemButton,
  MenuItem,
  Popover,
  Select,
  TableHead,
  Tooltip,
} from "@mui/material";
import DeleteIcon from "../../components/Icons/DeleteIcon";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowDownIcon from "@mui/icons-material/ArrowDownward";
import { COLORS } from "../../themes/Base";
import UrlsModal from "./UrlsModal";
import { isEmpty, orderBy } from "lodash";
import ArrowUpIcon from "@mui/icons-material/ArrowUpward";
import ChevronDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ChevronUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { countryCodes } from "../../types/countryCodes";
import CountryFilterDropdown from "../../components/CountryFilterDropdown";
import EditSupplierFormModal from "./EditSupplierFormModal";
import DeleteConfirmationDialog from "../../components/DeleteConfirmationDialog";
import { notify, notifySimple } from "../../themes/components/Notifications";
import { deleteProject } from "../../api/Project";

type Props = {
  project: Project;
  responses: ProjectResponsesTotal[];
  flagged_answers: ProjectFlaggedAnswersTotal[];
  refetch: () => void;
  project_suppliers_loi: ProjectSuppliersLOI;
  project_loi: ProjectLOI;
};

const SORT_OPTIONS_DROPDOWN = [
  { value: "name:asc", label: "Supplier (A-Z)" },
  { value: "name:desc", label: "Supplier (Z-A)" },
  { value: "country_code:asc", label: "Country Code (A–Z)" },
  { value: "country_code:desc", label: "Country Code (Z–A)" },
  { value: "last_complete_at:asc", label: "Last complete (Newest first)" },
  { value: "last_complete_at:desc", label: "Last complete (Oldest first)" },
];

const Project: React.FC<Props> = ({
  project,
  responses,
  flagged_answers,
  refetch,
  project_suppliers_loi,
  project_loi,
}) => {
  const [responsesModalBaseName, setResponsesModalBaseName] = useState(project.name);
  const [showResponsesModal, setShowResponsesModal] = useState<boolean>(false);
  const [showAnswersModal, setShowAnswersModal] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [showUrlsModal, setShowUrlsModal] = useState(false);
  const [modalSupplier, setModalSupplier] = useState<ProjectSupplier | null>(null);
  const [sortBy, setSortBy] = useState<string>("name:asc");
  const [countryFilter, setCountryFilter] = useState<Array<string>>([]);
  const [editProjectSupplier, setEditProjectSupplier] = useState<ProjectSupplier | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [flaggedModalSupplier, setFlaggedModalSupplier] = useState<ProjectSupplier | null>(null);
  const [responseModalSupplier, setResponseModalSupplier] = useState<ProjectSupplier | null>(null);

  const navigate = useNavigate();
  const { classes, cx } = useSharedStyles();

  const getLoiForProjectSupplier = (
    project_supplier_id: string,
    type: "completions" | "terminations" = "completions"
  ): number => {
    const result = project_suppliers_loi[type].find(
      (item: { loi_minutes: number; project_supplier_id: string }) =>
        item.project_supplier_id == project_supplier_id
    );
    if (result) {
      return Number(result.loi_minutes);
    } else {
      return 0;
    }
  };

  // filter suppliers (based on filter state) which is currently country code
  const filteredSuppliers = useMemo(() => {
    if (countryFilter.length === 0) return project.suppliers;
    return project.suppliers.filter(
      (supplier) => supplier.country_code && countryFilter.includes(supplier.country_code)
    );
  }, [project.suppliers, countryFilter]);

  // sort suppliers (sorting does not affect totals)
  const [sortField, sortOrderValue] = sortBy.split(":");
  const processedSuppliers = useMemo(() => {
    // sorting logic for all columns, including supplier name
    return orderBy(
      filteredSuppliers,
      [sortField === "name" ? (el) => el.source.name?.toLowerCase().trim() : (el) => el[sortField]],
      [sortOrderValue as "asc" | "desc"]
    );
  }, [filteredSuppliers, sortField, sortOrderValue]);

  // calculate totals only when filteredSuppliers changes
  const totals = useMemo(() => {
    const entrants = filteredSuppliers.reduce((acc, s) => acc + (s.entrants || 0), 0);
    const completed = filteredSuppliers.reduce((acc, s) => acc + (s.completed || 0), 0);
    const screenedOut = filteredSuppliers.reduce((acc, s) => acc + (s.screened_out || 0), 0);
    const terminated = filteredSuppliers.reduce((acc, s) => acc + (s.terminated || 0), 0);
    const quotaFull = filteredSuppliers.reduce((acc, s) => acc + (s.quota_full || 0), 0);
    // LOI must be calculating the median score on all responses - this is done in backend
    const loi = project_loi.completions;
    const termLoi = project_loi.terminations;
    const ir = filteredSuppliers.reduce((acc, s) => {
      // IR = completed / (completed + screened_out)
      const denominator = (s.completed || 0) + (s.screened_out || 0);
      return acc + (denominator ? s.completed / denominator : 0);
    }, 0);
    const drop_off = filteredSuppliers.reduce((acc, s) => {
      // Drop Off = (entrants - completed - quota_full - terminated - screened_out) / entrants
      const numerator =
        (s.entrants || 0) -
        (s.completed || 0) -
        (s.quota_full || 0) -
        (s.terminated || 0) -
        (s.screened_out || 0);
      return acc + (s.entrants ? numerator / s.entrants : 0);
    }, 0);
    const remaining = filteredSuppliers.reduce(
      (acc, s) => acc + ((s.limit || 0) - (s.completed || 0)),
      0
    );
    const limit = filteredSuppliers.reduce((acc, s) => acc + (s.limit || 0), 0);

    const deviceTerm = filteredSuppliers.reduce((acc, s) => acc + (s.device_forensiq_term || 0), 0);
    const roundtableTerm = filteredSuppliers.reduce((acc, s) => acc + (s.roundtable_term || 0), 0);
    const duplicateTerm = filteredSuppliers.reduce((acc, s) => acc + (s.duplicate_term || 0), 0);
    const clientRejectTerm = filteredSuppliers.reduce((acc, s) => acc + (s.client_reject_term || 0), 0);

    return {
      entrants,
      completed,
      screenedOut,
      terminated,
      quotaFull,
      loi,
      termLoi,
      ir: filteredSuppliers.length ? Math.round((ir / filteredSuppliers.length) * 100) : 0, // average IR %
      drop_off: filteredSuppliers.length
        ? Math.round((drop_off / filteredSuppliers.length) * 100)
        : 0, // average Drop Off %
      remaining,
      limit,
      deviceTerm,
      roundtableTerm,
      duplicateTerm,
      clientRejectTerm,
    };
  }, [filteredSuppliers]);

  const responsesMap = useMemo(
    () =>
      Object.fromEntries(
        responses.map((r) => [r.project_supplier_id, Number(r.total_responses) || 0])
      ),
    [responses]
  );

  const flaggedAnswersMap = useMemo(
    () =>
      Object.fromEntries(
        flagged_answers.map((f) => [f.project_supplier_id, Number(f.total_flagged_answers) || 0])
      ),
    [flagged_answers]
  );

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortBy(`${field}:${sortOrderValue === "asc" ? "desc" : "asc"}`);
    } else {
      setSortBy(`${field}:asc`);
    }
  };

  const renderSortArrow = (field: string) => {
    if (sortField === field) {
      return sortOrderValue === "asc" ? <ArrowDownIcon /> : <ArrowUpIcon />;
    }
    return <ArrowDownIcon />;
  };

  const availableCountryCodes = useMemo(() => {
    const codesInSuppliers = new Set(project.suppliers.map((s) => s.country_code));
    return countryCodes.filter((c) => codesInSuppliers.has(c.code));
  }, [project.suppliers]);

  const onDeleteSubmit = async () => {
    try {
      const response = await deleteProject(project?.id);
      const { messages, result } = response.projectDelete;

      if (!isEmpty(messages)) {
        notify(messages);
      } else if (result) {
        navigate("/projects");
      }
    } catch (err) {
      notifySimple(["Error deleting project"], "error");
    }
  };

  return (
    <>
      <Box padding="16px 32px" boxShadow="0 6px 24px 0 rgba(0, 0, 0, 0.05)">
        <Box height="44px" display="flex" mb="16px" alignItems="center">
          <NavLink
            to="/"
            style={{
              display: "flex",
              alignItems: "center",
              color: "#67676C",
              fontSize: 16,
              textDecoration: "none",
            }}
            className="back-link"
          >
            <ArrowBackIcon sx={{ fontSize: 20, mr: 1 }} />
            Back to Projects
          </NavLink>
        </Box>

        <Header
          spacing="0"
          project={project}
          refetch={refetch}
          onEdit={(event) => {
            setAnchorEl(event.currentTarget);
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "right" }}>
            <FormControl sx={{ width: 281, mr: 1 }}>
              <Select
                fullWidth
                value={sortBy}
                onChange={(e) => {
                  setSortBy(e.target.value);
                }}
                renderValue={(selected) => {
                  const selectedOption = SORT_OPTIONS_DROPDOWN.find((opt) => opt.value === selected);
                  return selectedOption ? (
                    <span style={{ color: "#67676C" }}>{`Sort by: ${selectedOption.label}`}</span>
                  ) : (
                    ""
                  );
                }}
                IconComponent={(props) =>
                  props.className?.includes("MuiSelect-iconOpen") ? (
                    <ChevronUpIcon sx={{ color: COLORS.iconColor }} />
                  ) : (
                    <ChevronDownIcon sx={{ color: COLORS.iconColor }} />
                  )
                }
                MenuProps={{
                  PaperProps: {
                    className: classes.innerBox,
                  },
                }}
              >
                {SORT_OPTIONS_DROPDOWN.map((option) => (
                  <MenuItem
                    key={option.value}
                    value={option.value}
                    sx={{
                      fontSize: 14,
                      margin: "8px",
                      "&.Mui-selected, &:hover, &.Mui-selected:hover": {
                        backgroundColor: "#EAEAED",
                        color: "inherit",
                        borderRadius: "8px",
                      },
                    }}
                  >
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl sx={{ width: 220 }}>
              <CountryFilterDropdown
                value={countryFilter}
                onChange={setCountryFilter}
                options={availableCountryCodes}
                supplierCount={(code) => project.suppliers.filter((s) => s.country_code === code).length}
              />
            </FormControl>
          </Box>
        </Header>
      </Box>
      <Box
        sx={{
          width: "100%",
          overflow: "auto",
          position: "relative",
          height: "calc(100vh - 153px)",
        }}
      >
        <Table stickyHeader sx={{ height: "100%" }}>
          <TableHead>
            <TableRow>
              <TableCell
                className={classes.tableHeaderCell}
                sx={{
                  position: "sticky",
                  left: 0,
                  top: 0,
                  boxShadow: "6px 0 24px 0 rgba(0, 0, 0, 0.03)",
                  zIndex: 9,
                  textAlign: "left !important",
                }}
              >
                Suppliers
              </TableCell>
              <TableCell className={classes.tableHeaderCell} onClick={() => handleSort("entrants")}>
                Entrants {renderSortArrow("entrants")}
              </TableCell>
              <TableCell
                className={classes.tableHeaderCell}
                onClick={() => handleSort("completed")}
              >
                Completed {renderSortArrow("completed")}
              </TableCell>
              <TableCell
                className={classes.tableHeaderCell}
                onClick={() => handleSort("screened_out")}
              >
                Screened Out {renderSortArrow("screened_out")}
              </TableCell>
              <TableCell
                className={classes.tableHeaderCell}
                onClick={() => handleSort("terminated")}
              >
                Quality Term {renderSortArrow("terminated")}
              </TableCell>
              <TableCell
                className={classes.tableHeaderCell}
                onClick={() => handleSort("quota_full")}
              >
                Quota Full {renderSortArrow("quota_full")}
              </TableCell>
              <TableCell className={classes.tableHeaderCell} onClick={() => handleSort("loi")}>
                LOI {renderSortArrow("loi")}
              </TableCell>
              <TableCell className={classes.tableHeaderCell} onClick={() => handleSort("term_loi")}>
                Term LOI {renderSortArrow("term_loi")}
              </TableCell>
              <TableCell className={classes.tableHeaderCell} onClick={() => handleSort("ir")}>
                IR {renderSortArrow("ir")}
              </TableCell>
              <TableCell className={classes.tableHeaderCell} onClick={() => handleSort("drop_off")}>
                Drop Off {renderSortArrow("drop_off")}
              </TableCell>
              <TableCell
                className={classes.tableHeaderCell}
                onClick={() => handleSort("remaining")}
              >
                Remaining {renderSortArrow("remaining")}
              </TableCell>
              <TableCell
                className={classes.tableHeaderCell}
                sx={{
                  position: "sticky",
                  right: 0,
                  zIndex: 6,
                  minWidth: "50px !important",
                  maxWidth: "50px !important",
                }}
              >
                Action
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {processedSuppliers.map((supplier, idx) => (
              <TableRow key={`${supplier.id}-${idx}`}>
                <TableCell
                  sx={{
                    position: "sticky",
                    left: 0,
                    background: "#FEFFF8",
                    minWidth: 420,
                    boxShadow: "6px 0 24px 0 rgba(0, 0, 0, 0.03)",
                    padding: "16px 24px",
                    borderBottom: "1px solid #E4E8D3",
                  }}
                >
                  <Box sx={{ fontSize: "16px" }}>{supplier.source.name}</Box>

                  <Box sx={{ color: COLORS.textSecondary, fontSize: "14px" }}>
                    {supplier.description ? (
                      <p style={{ margin: 0 }}>Description: {supplier.description}</p>
                    ) : null}
                    {supplier.country_code ? (
                      <p style={{ margin: 0 }}>Country code: {supplier.country_code}</p>
                    ) : null}
                    <div>
                      Last complete:{" "}
                      {supplier.last_complete_at
                        ? getLocalTime(supplier.last_complete_at).fromNow()
                        : "never"}
                    </div>
                  </Box>
                  <Box
                    sx={{
                      mt: "16px",
                      display: "flex",
                      background: "#fff",
                      alignItems: "center",
                      border: "1px solid #E4E8D3",
                      borderRadius: "10px",
                      fontSize: "12px",
                      color: COLORS.textPrimary,
                      overflow: "hidden",
                      width: "fit-content",
                    }}
                  >
                    <Box
                      sx={{
                        p: 1,
                        px: 2,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        cursor: "pointer",
                        "&:last-child": { borderRight: "none" },
                      }}
                      onClick={() => {
                        setModalSupplier(supplier);
                        setShowUrlsModal(true);
                      }}
                    >
                      Show URLs
                    </Box>
                    <Divider orientation="vertical" flexItem sx={{ mx: 0.5, bgcolor: "#E4E8D3" }} />
                    <Box
                      sx={{
                        p: 1,
                        px: 2,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        cursor: "pointer",
                        "&:last-child": { borderRight: "none" },
                      }}
                      onClick={() => {
                        setResponseModalSupplier(supplier);
                        setResponsesModalBaseName(`${project.name}-${supplier.source.name}`);
                        setShowResponsesModal(true);
                      }}
                    >
                      Responses ({numberFormatterFull.format(responsesMap[supplier.id] || 0)})
                    </Box>
                    <Divider orientation="vertical" flexItem sx={{ mx: 0.5, bgcolor: "#E4E8D3" }} />
                    <Box
                      sx={{
                        p: 1,
                        px: 2,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        cursor: supplier.pre_survey_tool ? "pointer" : "default",
                        color: supplier.pre_survey_tool ? "#67676C" : "#BDBDBD",
                      }}
                      onClick={() => {
                        if (supplier.pre_survey_tool) {
                          setFlaggedModalSupplier(supplier);
                          setResponsesModalBaseName(`${project.name}-${supplier.source.name}`);
                          setShowAnswersModal(true);
                        }
                      }}
                    >
                      Flagged answers ({numberFormatterFull.format(flaggedAnswersMap[supplier.id] || 0)})
                    </Box>
                  </Box>
                </TableCell>
                <TableCell className={classes.tableBodyCell}>
                  {numberFormatterFull.format(supplier.entrants)}
                </TableCell>
                <TableCell className={classes.tableBodyCell}>
                  {numberFormatterFull.format(supplier.completed)}
                </TableCell>
                <TableCell className={classes.tableBodyCell}>
                  {numberFormatterFull.format(supplier.screened_out)}
                </TableCell>
                <TableCell className={classes.tableBodyCell}>
                  {/* Breakdown */}
                  <Tooltip
                    title={
                      <Box
                        sx={{ fontSize: 12, color: COLORS.textSecondary, mt: 0.5, textAlign: "left" }}
                      >
                        <div>Device Forensiq: {numberFormatterFull.format(supplier.device_forensiq_term || 0)} </div>
                        <div>Roundtable: {numberFormatterFull.format(supplier.roundtable_term || 0)} </div>
                        <div>Duplication: {numberFormatterFull.format(supplier.duplicate_term || 0)} </div>
                        <div>Client Rejection: {numberFormatterFull.format(supplier.client_reject_term || 0)}</div>
                      </Box>
                    }
                    arrow
                    placement="bottom"
                    slotProps={{
                      tooltip: {
                        className: classes.terminatedBreakdown,
                      },
                      arrow: {
                        sx: { color: "#fff" },
                      },
                    }}
                  >
                    <span>{numberFormatterFull.format(supplier.terminated)}</span>
                  </Tooltip>
                </TableCell>
                <TableCell className={classes.tableBodyCell}>
                  {numberFormatterFull.format(supplier.quota_full)}
                </TableCell>
                <TableCell className={classes.tableBodyCell}>
                  {getLoiForProjectSupplier(supplier.id)} min
                </TableCell>
                <TableCell className={classes.tableBodyCell}>
                  {getLoiForProjectSupplier(supplier.id, "terminations")} min
                </TableCell>
                <TableCell className={classes.tableBodyCell}>
                  {Math.round(
                    (supplier.completed
                      ? Number(supplier.completed) /
                      (Number(supplier.completed) + Number(supplier.screened_out))
                      : 0) * 100
                  )}
                  %
                </TableCell>
                <TableCell className={classes.tableBodyCell}>
                  {Math.round(
                    (supplier.entrants
                      ? (supplier.entrants -
                        supplier.completed -
                        supplier.quota_full -
                        supplier.terminated -
                        supplier.screened_out) /
                      supplier.entrants
                      : 0) * 100
                  )}
                  %
                </TableCell>
                <TableCell className={classes.tableBodyCell}>
                  {numberFormatterFull.format(supplier.limit - supplier.completed)}
                  &nbsp;/&nbsp;
                  {numberFormatterFull.format(supplier.limit)}
                </TableCell>
                <TableCell
                  sx={{
                    position: "sticky",
                    right: 0,
                    background: "#FEFFF8",
                    zIndex: 5,
                    minWidth: "50px !important",
                    boxShadow: "-6px 0 24px 0 rgba(0, 0, 0, 0.03)",
                  }}
                >
                  <IconButton onClick={() => setEditProjectSupplier(supplier)}>
                    <EditIcon sx={{ color: COLORS.iconColor }} />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {/* Spacer row to push footer to the bottom - tried a lot but couldn't not get it working via css */}
            {processedSuppliers.length < 4 && (
              <TableRow>
                <TableCell colSpan={12} sx={{ border: 0, padding: 0, height: "100%" }}>
                  <Box
                    sx={{
                      height: `${(3 - processedSuppliers.length) * 160}px`,
                    }}
                  />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TableCell
                className={classes.tableFooterCell}
                sx={{
                  position: "sticky",
                  left: 0,
                  bottom: 0,
                  background: "#FEFFF8",
                  zIndex: "11 !important",
                  textAlign: "left !important",
                }}
              >
                Totals:
              </TableCell>
              <TableCell className={classes.tableFooterCell}>
                {numberFormatterFull.format(totals.entrants)}
              </TableCell>
              <TableCell className={classes.tableFooterCell}>
                <div className={cx(classes.itemStatsNumber, "positive", "total")}>
                  {numberFormatterFull.format(totals.completed)}
                </div>
              </TableCell>
              <TableCell className={classes.tableFooterCell}>
                <div className={cx(classes.itemStatsNumber, "negative", "total")}>
                  {numberFormatterFull.format(totals.screenedOut)}
                </div>
              </TableCell>
              <TableCell className={classes.tableFooterCell}>
                <div className={cx(classes.itemStatsNumber, "negative", "total")}>
                  {/* Breakdown */}
                  <Tooltip
                    title={
                      <Box sx={{ fontSize: 12, mt: 0.5, textAlign: "left", lineHeight: "16px", color: COLORS.textSecondary }}>
                        <div>Device Forensiq: {numberFormatterFull.format(totals.deviceTerm)} </div>
                        <div>Roundtable: {numberFormatterFull.format(totals.roundtableTerm)} </div>
                        <div>Duplication: {numberFormatterFull.format(totals.duplicateTerm)} </div>
                        <div>Client Rejection: {numberFormatterFull.format(totals.clientRejectTerm)}</div>
                      </Box>
                    }
                    arrow
                    placement="bottom"
                    slotProps={{
                      tooltip: {
                        className: classes.terminatedBreakdown,
                      },
                      arrow: {
                        sx: { color: "#fff" },
                      },
                    }}
                  >
                    <span>{numberFormatterFull.format(totals.terminated)}</span>
                  </Tooltip>
                </div>
              </TableCell>
              <TableCell className={classes.tableFooterCell}>
                {numberFormatterFull.format(totals.quotaFull)}
              </TableCell>
              <TableCell className={classes.tableFooterCell}>
                {numberFormatterFull.format(totals.loi)} min
              </TableCell>
              <TableCell className={classes.tableFooterCell}>
                {numberFormatterFull.format(totals.termLoi)}
              </TableCell>
              <TableCell className={classes.tableFooterCell}>{totals.ir}%</TableCell>
              <TableCell className={classes.tableFooterCell}>{totals.drop_off}%</TableCell>
              <TableCell className={classes.tableFooterCell}>
                {numberFormatterFull.format(project.completed_responses_limit - totals.completed)}&nbsp;/&nbsp;
                {project.completed_responses_limit}
              </TableCell>
              <TableCell
                className={classes.tableFooterCell}
                sx={{
                  position: "sticky",
                  right: 0,
                  bottom: 0,
                  background: "#FEFFF8",
                  zIndex: 4,
                }}
              >
                &nbsp;
              </TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </Box>

      <Popover
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        disableScrollLock
        PaperProps={{
          className: cx(classes.innerBox),
        }}
      >
        <List className={classes.dropdownMenu}>
          <ListItemButton
            onClick={() => {
              setAnchorEl(null);
              setResponsesModalBaseName(project.name);
              setResponseModalSupplier(null);
              setShowResponsesModal(true);
            }}
            className={classes.dropdownMenuItem}
          >
            <VisibilityIcon /> View All Responses
          </ListItemButton>
          <ListItemButton
            onClick={() => {
              setAnchorEl(null);
              navigate(`/project/${project?.id}/edit`);
            }}
            className={classes.dropdownMenuItem}
          >
            <EditIcon /> Edit Project
          </ListItemButton>
          <Divider />
          <ListItemButton
            onClick={() => {
              setAnchorEl(null);
              setShowDeleteModal(true);
            }}
            className={cx(classes.dropdownMenuItem, "red")}
          >
            <DeleteIcon /> Delete Project
          </ListItemButton>
        </List>
      </Popover>

      <ResponsesModal
        open={!!showResponsesModal}
        setOpen={(open) => {
          setShowResponsesModal(open);
          if (!open) setResponseModalSupplier(null);
        }}
        baseName={responsesModalBaseName}
        projectId={project.id}
        projectSupplier={responseModalSupplier}
        totalResponses={
          responseModalSupplier
            ? responsesMap[responseModalSupplier.id] || 0
            : // If no supplier, sum all responses
            Object.values(responsesMap).reduce((acc, n) => acc + n, 0)
        }
      />

      <AnswersModal
        projectSupplier={flaggedModalSupplier}
        setOpen={(open) => {
          setShowAnswersModal(open);
          if (!open) setFlaggedModalSupplier(null);
        }}
        open={showAnswersModal}
        baseName={responsesModalBaseName}
      />

      <UrlsModal
        open={showUrlsModal}
        onClose={() => {
          setShowUrlsModal(false);
          setModalSupplier(null);
        }}
        supplier={modalSupplier}
        project={project}
      />

      <EditSupplierFormModal
        open={!!editProjectSupplier}
        onClose={() => setEditProjectSupplier(null)}
        projectSupplier={editProjectSupplier}
        projectId={project.id}
        refetch={refetch}
      />

      <DeleteConfirmationDialog
        open={showDeleteModal}
        onCancel={() => setShowDeleteModal(false)}
        onConfirm={() => {
          void onDeleteSubmit();
        }}
        title="Are you sure you want to delete this project?"
        description="Deleting this project will permanently remove all associated data. This action cannot be undone."
      />
    </>
  );
};

export default Project;
